import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { UserProfileService } from '../../services/user-profile.service';
import { validatePassword } from '../../utils/passwordPolicy';

/**
 * OWASP A07 round 2: after an admin sets or resets a password, the login token carries
 * `mustChangePassword` and the backend (ActiveStatusFilter) refuses everything except the self
 * password change. This screen sits outside the app shell so nothing else loads first.
 * The server ends every session on the change, so the user logs in again with the new password.
 */
const ChangePasswordRequired = () => {
    const { user, logout } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    const [previousPassword, setPreviousPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    if (!AuthService.isValidToken()) {
        return <Navigate to="/login" replace />;
    }

    const newPasswordErrors = newPassword ? validatePassword(newPassword, user?.username) : [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('New password and confirm password do not match');
            return;
        }
        if (validatePassword(newPassword, user?.username).length > 0) {
            setError('Password does not meet the strength requirements');
            return;
        }
        setLoading(true);
        try {
            await UserProfileService.updatePassword({ previousPassword, newPassword });
            toast.showSuccess('Password updated. Please log in with your new password.');
            logout();
            navigate('/login', { replace: true });
        } catch (err: any) {
            const body = err?.response?.data;
            const text = typeof body === 'string' ? body : body?.message;
            if (typeof text === 'string' && /previous password/i.test(text)) {
                setError('The temporary password is incorrect');
            } else if (typeof text === 'string' && text) {
                setError(text);
            } else {
                setError('Failed to update password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        logout();
        navigate('/login', { replace: true });
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 6 }}>
            <Paper sx={{ p: 4, width: '100%', maxWidth: 440 }} component="form" onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>Set a new password</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your password was set by an administrator. Choose your own password to continue.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    label="Temporary password"
                    type="password"
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="current-password"
                    value={previousPassword}
                    onChange={e => setPreviousPassword(e.target.value)}
                />
                <TextField
                    label="New password"
                    type="password"
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    error={newPasswordErrors.length > 0}
                    helperText={newPasswordErrors.join(' ')}
                />
                <TextField
                    label="Confirm new password"
                    type="password"
                    fullWidth
                    margin="normal"
                    required
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button type="submit" variant="contained" disabled={loading}>Save password</Button>
                    <Button onClick={handleCancel} disabled={loading}>Log out</Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default ChangePasswordRequired;
